import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authApi, type LoginPayload } from "@/modules/auth/api/auth.api";
import { useAuthStore } from "@/modules/auth/store/auth.store";

/**
 * Auth hook — store + actions birlashtirilgan API.
 *
 * Komponentlar `const { user, login, logout, switchTenant } = useAuth()` qiladi.
 */
export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const currentTenant = useAuthStore((s) => s.currentTenant);
  const tenants = useAuthStore((s) => s.tenants);
  const isHydrating = useAuthStore((s) => s.isHydrating);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clear = useAuthStore((s) => s.clear);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: async (snapshot) => {
      // Single-tenant Interwork — switch yo'q
      setAuth(snapshot);
      queryClient.invalidateQueries();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clear();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  const switchTenantMutation = useMutation({
    mutationFn: async (_tenantId: string) => {
      // Single-tenant: no-op, joriy snapshot
      const store = useAuthStore.getState();
      if (!store.user || !store.currentTenant) {
        throw new Error("login required");
      }
      return {
        accessToken: store.accessToken!,
        accessTokenExpiresAt: store.accessTokenExpiresAt,
        user: store.user,
        currentTenant: store.currentTenant,
        tenants: store.tenants,
      };
    },
    onSuccess: () => {
      /* single-tenant: no switch UI */
    },
  });

  const login = useCallback(
    (payload: LoginPayload) => loginMutation.mutateAsync(payload),
    [loginMutation],
  );

  const logout = useCallback(() => logoutMutation.mutate(), [logoutMutation]);

  const switchTenant = useCallback(
    (tenantId: string) => switchTenantMutation.mutateAsync(tenantId),
    [switchTenantMutation],
  );

  return {
    // holat
    isAuthenticated: Boolean(accessToken && user),
    isHydrating,
    user,
    currentTenant,
    tenants,
    // mutationlar
    login,
    logout,
    switchTenant,
    loginPending: loginMutation.isPending,
    loginError: loginMutation.error,
    switchPending: switchTenantMutation.isPending,
  };
}
